<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          :showAddNew="false"
          title="Import Fixed Asset"
      >
        <template #extra-btn>
          <a
              download
              href="/file/import-fixed-asset.xlsx"
              class="btn btn-outline-secondary waves-effect waves-float waves-light"
          >Download Sample
          </a>
        </template>
      </TitleButton>
      <input type="file" class="d-none" ref="uploader" @change="handleFileUpload">
    </div>

    <div class="row p-2">
      <div class="col-12 col-sm-6 col-md-4 col-lg-2 mt-auto">
        <button @click="uploadCsv" class="btn btn-primary waves-effect waves-float waves-light">
          <i class="fas fa-upload me-1"></i> Browse
        </button>
      </div>
    </div>

    <div class="px-2 pb-2">
      <div v-if="batchRows.length < 1" class="empty-state text-center py-3">
        <i class="fas fa-file-excel empty-state-icon"></i>
        <p class="mb-0">No datasets uploaded yet</p>
        <p class="empty-state-hint">Browse an Excel file above to get started</p>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-bordered table-hover align-middle">
          <thead>
          <tr>
            <th>ID</th>
            <th>Dataset</th>
            <th>Status</th>
            <th class="text-end">Action</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in batchRows" :key="item.id">
            <td>{{ item.id }}</td>
            <td>
              <i class="fas fa-file-excel me-1" style="color: #1d6f42;"></i>
              {{ item.batch }}
            </td>
            <td>
              <span :class="statusClass(item.status)">{{ item.status }}</span>
            </td>
            <td class="text-end">
              <button
                  class="btn btn-primary btn-sm me-1"
                  @click="gotoReview(item.id)"
              >
                <i class="fas fa-eye me-1"></i> Review
              </button>
              <button
                  class="btn btn-icon btn-outline-danger btn-sm"
                  title="Delete dataset"
                  @click="deleteRow(item.id)"
              >
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <GlobalLoader/>
  </div>
</template>

<script setup>
import {ref, onMounted, inject} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import TitleButton from '@/components/atom/TitleButton.vue'
import GlobalLoader from '@/components/atom/GlobalLoader.vue'
import handleAssetRegister from '@/services/modules/assetRegister/assetRegister'

const route = useRoute()
const router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const {uploadAssetRegisterCsv, fetchAssetRegisterImportBatches, deleteAssetRegisterImportBatch} = handleAssetRegister()

const uploader = ref(null)
const batchRows = ref([])

function getQuery() {
  return `?company_id=${route.params.companyId}`
}

function loadBatches() {
  fetchAssetRegisterImportBatches(getQuery()).then(res => {
    if (!res.status) {
      batchRows.value = []
      return
    }
    batchRows.value = res.data
  })
}

function statusClass(status) {
  if (status === 'processing') return 'badge bg-warning'
  if (status === 'completed') return 'badge bg-success'
  return 'badge bg-secondary'
}

function uploadCsv() {
  uploader.value.click()
}

function gotoReview(id) {
  route.query.rowId = id
  router.push({name: 'import-fixed-asset-review', params: route.params, query: route.query})
}

function deleteRow(id) {
  if (!confirm('Are you sure you want to delete this dataset?')) return

  deleteAssetRegisterImportBatch(id, getQuery()).then(res => {
    if (!res.status) {
      showError(res.message)
      return
    }
    showSuccess(res.message)
    loadBatches()
  })
}

function handleFileUpload(event) {
  if (!event.target.files.length) return

  const file = event.target.files[0]
  const fd = new FormData()
  fd.append('company_id', route.params.companyId)
  fd.append('sheet', file)

  uploadAssetRegisterCsv(fd).then(res => {
    if (!res.status) {
      showError(res.message)
      return
    }
    showSuccess(res.message)
    loadBatches()
  }).catch(() => showError('Something went wrong')).finally(() => {
    event.target.value = ''
  })
}

onMounted(() => {
  loadBatches()
})
</script>

<style scoped>
.empty-state {
  color: #6c757d;
}
.empty-state-icon {
  font-size: 40px;
  color: #c9d3dd;
  display: block;
  margin-bottom: 8px;
}
.empty-state-hint {
  font-size: 13px;
  margin-bottom: 0;
}
.btn-icon {
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
